/**
 * TaskQuickCreateDialog - Linear-inspired centered modal for quick task creation.
 *
 * Minimal, focused UI: title input (auto-focused), description textarea,
 * inline property chips (status, priority, assignee, board), and a more-actions menu.
 * Creates the task and navigates to the detail page on success.
 */

import { useState, useCallback, useRef, useEffect, useLayoutEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { usePasteFilesInto } from '@/shared/files';
import { useNavigate } from 'react-router-dom';
import {
  Circle,
  SignalLow,
  SignalMedium,
  SignalHigh,
  AlertTriangle,
  MoreHorizontal,
  Plus,
  CalendarDays,
  Timer,
  TrendingUp,
  ListPlus,
  Loader2,
  X,
  Check,
  Kanban,
  Paperclip,
} from 'lucide-react';
import { format } from 'date-fns';
import { cn } from '../../../lib/utils';
import { Button } from '../../../components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../../components/ui/dropdown-menu';
import { Input } from '../../../components/ui/input';
import { BidiText, DialogForm, NumericFieldBox, UserPicker } from '../../../shared/components/common';
import { Calendar } from '../../../components/ui/calendar';
import { useCreateTask, useUpdateTask } from '../hooks/useTasks';
import { useBoards } from '../hooks/useBoards';
import { useColumns } from '../hooks/useColumns';
import { useUserPreferences } from '../../../shared/context';
import { ItemCreate, ItemStatus, ItemPriority, DropdownOption, Board } from '../types';
import { DEFAULT_STATUS_OPTIONS, getPriorityLabel, resolveDeclaredOption, resolvePriorityOptions } from '../constants/taskFields';
import { useUploadFile } from '@/shared/files/hooks/useFiles';

// --- Priority icons ---
const PRIORITY_ICONS: Record<string, React.ElementType> = {
  low: SignalLow,
  medium: SignalMedium,
  high: SignalHigh,
  critical: AlertTriangle,
};

function getPriorityIcon(priority: string) {
  return PRIORITY_ICONS[priority] || SignalMedium;
}


// --- Props ---
interface TaskQuickCreateDialogProps {
  open: boolean;
  onClose: () => void;
  /** Pre-selected board ID (when creating from within a board) */
  boardId?: string | null;
  /** Default status */
  defaultStatus?: ItemStatus;
  /** Default priority */
  defaultPriority?: ItemPriority;
  /** Default assignee */
  defaultAssignee?: string | null;
  /** Default parent item (for sub-tasks) */
  defaultParentItemId?: string;
  /** Default sprint */
  defaultSprintId?: string;
  /** Whether to navigate to the created task detail page */
  navigateOnCreate?: boolean;
  /**
   * Called after a task is successfully created, with the new task id + its
   * board id. When provided it takes precedence over `navigateOnCreate` — the
   * host decides how to surface the new task (e.g. open the detail panel in
   * place without a full navigation). Lets a board page deterministically open
   * the side panel after quick-create.
   */
  onCreated?: (taskId: string, boardId: string) => void;
}

export function TaskQuickCreateDialog({
  open,
  onClose,
  boardId: initialBoardId,
  defaultStatus,
  defaultPriority,
  defaultAssignee,
  defaultParentItemId,
  defaultSprintId,
  navigateOnCreate = true,
  onCreated,
}: TaskQuickCreateDialogProps) {
  const { t } = useTranslation(['tasks', 'common']);
  const navigate = useNavigate();
  const { formatPlanningDate } = useUserPreferences();
  const createMutation = useCreateTask();
  const updateMutation = useUpdateTask();
  const uploadMutation = useUploadFile();
  const titleRef = useRef<HTMLInputElement>(null);

  // Form state
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [status, setStatus] = useState<string>(defaultStatus || 'todo');
  const [priority, setPriority] = useState<string>(defaultPriority || '');
  const [assignee, setAssignee] = useState<string | null>(defaultAssignee || null);
  const [selectedBoardId, setSelectedBoardId] = useState<string | null>(initialBoardId || null);
  const [dueDate, setDueDate] = useState<Date | undefined>(undefined);
  const [estimatedHours, setEstimatedHours] = useState<number | undefined>(undefined);
  const [showEstimate, setShowEstimate] = useState(false);
  const [progress, setProgress] = useState<number | undefined>(undefined);
  const [showProgress, setShowProgress] = useState(false);
  const [subtaskTitles, setSubtaskTitles] = useState<string[]>([]);
  const [showSubtasks, setShowSubtasks] = useState(false);
  const [attachments, setAttachments] = useState<File[]>([]);
  const dialogBodyRef = useRef<HTMLDivElement>(null);
  // Paste anywhere in the dialog joins the same pending list the paper-clip fills.
  usePasteFilesInto({
    scopeRef: dialogBodyRef,
    onFiles: (files) => setAttachments((prev) => [...prev, ...files]),
  });
  const descriptionRef = useRef<HTMLTextAreaElement>(null);
  const subtaskInputRef = useRef<HTMLInputElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Auto-resize description textarea to fit content
  const autoResizeDescription = useCallback(() => {
    const el = descriptionRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  }, []);

  useLayoutEffect(() => {
    if (open) autoResizeDescription();
  }, [description, open, autoResizeDescription]);

  // Fetch all boards for the board picker
  const { data: boardsData } = useBoards({ enabled: open });
  const boards = boardsData?.data || [];

  // Fetch columns for selected board (for status options)
  const { data: columnsData } = useColumns(selectedBoardId || '');
  const columns = columnsData?.data || [];

  // Resolve status options from board columns
  const statusColumn = columns.find((col) => col.column_key === 'status');
  const statusOptions: DropdownOption[] = statusColumn?.settings?.options && statusColumn.settings.options.length > 0
    ? statusColumn.settings.options
    : DEFAULT_STATUS_OPTIONS;

  // The status this dialog will actually send — see `resolveDeclaredOption`.
  const effectiveStatus = resolveDeclaredOption(status, statusOptions);

  // The board's own priority vocabulary, exactly like the status control above.
  const boardPriorityOptions = resolvePriorityOptions(columns);
  const priorityOptions: DropdownOption[] = boardPriorityOptions.map((opt) => ({
    ...opt,
    label: getPriorityLabel(opt.id, boardPriorityOptions, t),
  }));

  // Selected board object
  const selectedBoard = boards.find((b: Board) => b.id === selectedBoardId);

  // Auto-select first board if none selected
  const firstBoardId = boardsData?.data?.[0]?.id;
  useEffect(() => {
    if (open && !selectedBoardId && firstBoardId) {
      setSelectedBoardId(firstBoardId);
    }
  }, [open, selectedBoardId, firstBoardId]);

  // Reset form when dialog opens
  useEffect(() => {
    if (open) {
      setTitle('');
      setDescription('');
      setStatus(defaultStatus || 'todo');
      setPriority(defaultPriority || '');
      setAssignee(defaultAssignee || null);
      setDueDate(undefined);
      setEstimatedHours(undefined);
      setShowEstimate(false);
      setProgress(undefined);
      setShowProgress(false);
      setSubtaskTitles([]);
      setShowSubtasks(false);
      setAttachments([]);
      if (initialBoardId) {
        setSelectedBoardId(initialBoardId);
      }
      // Focus title input after render
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          titleRef.current?.focus();
        });
      });
    }
  }, [open, defaultStatus, defaultPriority, defaultAssignee, initialBoardId]);

  const handleSubmit = useCallback(async () => {
    if (!title.trim()) return;

    try {
      const data: ItemCreate = {
        title: title.trim(),
        description: description.trim() || undefined,
        status: effectiveStatus || undefined,
        priority: (priority || undefined) as ItemPriority | undefined,
        assigned_to: assignee || undefined,
        parent_task_id: defaultParentItemId || undefined,
        sprint_id: defaultSprintId || undefined,
        due_date: dueDate ? format(dueDate, 'yyyy-MM-dd') : undefined,
        estimated_hours: estimatedHours || undefined,
      };

      const result = await createMutation.mutateAsync({
        boardId: selectedBoardId,
        data,
      });

      const parentId = result.data?.id;

      // Set progress if provided (not supported on create, requires separate update)
      if (parentId && progress != null) {
        await updateMutation.mutateAsync({ id: parentId, data: { percent_complete_override: progress } });
      }

      // Upload attachments
      if (parentId && attachments.length > 0) {
        await Promise.all(
          attachments.map((file) =>
            uploadMutation.mutateAsync({
              file,
              attached_to: `tasks:${parentId}`,
              is_user_file: false,
            }),
          ),
        );
      }

      // Create subtasks
      const validSubtasks = subtaskTitles.filter((t) => t.trim());
      if (parentId && validSubtasks.length > 0) {
        await Promise.all(
          validSubtasks.map((subtaskTitle) =>
            createMutation.mutateAsync({
              boardId: selectedBoardId,
              data: { title: subtaskTitle.trim(), parent_task_id: parentId },
            }),
          ),
        );
      }

      const boardId = selectedBoardId || result.data?.board_id;
      onClose();
      if (parentId && boardId) {
        if (onCreated) {
          // Host opens the new task in place (e.g. board detail panel).
          onCreated(parentId, boardId);
        } else if (navigateOnCreate) {
          // The board detail page reads the open-task id from the `task` query
          // param (see BoardDetailPage ITEM_PARAM) — must match that key.
          navigate(`/tasks/boards/${boardId}?task=${parentId}`);
        }
      }
    } catch {
      // The global mutation-error handler shows the specific translated toast.
    }
  }, [
    title, description, effectiveStatus, priority, assignee, selectedBoardId,
    dueDate, estimatedHours, progress, subtaskTitles, attachments, defaultParentItemId,
    defaultSprintId, createMutation, updateMutation, uploadMutation, onClose, navigateOnCreate, onCreated, navigate,
  ]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
      e.preventDefault();
      handleSubmit();
    }
    if (e.key === 'Escape') {
      e.preventDefault();
      onClose();
    }
  }, [handleSubmit, onClose]);

  // Resolve current labels
  const currentPriorityOption = priorityOptions.find((o) => o.id === priority);
  const PriorityIcon = priority ? getPriorityIcon(priority) : MoreHorizontal;

  const formContent = (
    <div className="flex flex-col" onKeyDown={handleKeyDown}>
      {/* Title input */}
      <input
        ref={titleRef}
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        placeholder={t('tasks:fields.title')}
        className={cn(
          'w-full bg-transparent text-lg font-medium px-5 pt-1 pb-1',
          'placeholder:text-muted-foreground/50 outline-none border-none',
        )}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && !e.shiftKey && !e.metaKey && !e.ctrlKey) {
            e.preventDefault();
            descriptionRef.current?.focus();
          }
        }}
      />

      {/* Description textarea — auto-grows with content, scrolls after max-h */}
      <textarea
        ref={descriptionRef}
        value={description}
        onChange={(e) => setDescription(e.target.value)}
        placeholder={t('tasks:task.descriptionPlaceholder')}
        rows={4}
        className={cn(
          'w-full bg-transparent text-sm px-5 py-1 resize-none',
          'placeholder:text-muted-foreground/40 outline-none border-none',
          'min-h-[100px] max-h-[50vh] overflow-y-auto',
        )}
      />

      {/* Property buttons row */}
      <div className="flex items-center gap-1.5 py-2.5 border-t border-border flex-wrap">
        {/* Status */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="sm" className="h-7 px-2 text-xs gap-1.5">
              <Circle
                className="h-3 w-3"
                style={{ color: statusOptions.find((o) => o.id === effectiveStatus)?.color || 'hsl(var(--muted-foreground))' }}
                fill="currentColor"
              />
              {statusOptions.find((o) => o.id === effectiveStatus)?.label || effectiveStatus}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start">
            {statusOptions.map((option) => (
              <DropdownMenuItem
                key={option.id}
                className="gap-2"
                onSelect={() => setStatus(option.id)}
              >
                <Circle
                  className="h-3 w-3"
                  style={{ color: option.color || 'hsl(var(--muted-foreground))' }}
                  fill="currentColor"
                />
                {option.label}
                {option.id === effectiveStatus && <Check className="h-3.5 w-3.5 ms-auto" />}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>

        {/* Priority */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="sm" className="h-7 px-2 text-xs gap-1.5">
              <PriorityIcon className="h-3 w-3" />
              {currentPriorityOption?.label || t('tasks:fields.priority')}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start">
            {priorityOptions.map((option) => {
              const OptionIcon = getPriorityIcon(option.id);
              return (
                <DropdownMenuItem
                  key={option.id}
                  className="gap-2"
                  onSelect={() => setPriority(option.id)}
                >
                  <OptionIcon className="h-3.5 w-3.5" />
                  {option.label}
                  {option.id === priority && <Check className="h-3.5 w-3.5 ms-auto" />}
                </DropdownMenuItem>
              );
            })}
          </DropdownMenuContent>
        </DropdownMenu>

        {/* Assignee */}
        <div className="[&_button]:!h-7 [&_button]:!px-2 [&_button]:!text-xs [&_button]:!min-h-0 [&_button]:!gap-1.5 [&_button]:!w-auto [&_button]:!justify-start [&_button]:!text-foreground [&_svg.lucide-chevrons-up-down]:!hidden">
          <UserPicker
            value={assignee}
            onChange={setAssignee}
            placeholder={t('tasks:fields.assignee')}
            filterUserTypes={['internal', 'ai_agent']}
            size="sm"
            compact
            fullWidth={false}
          />
        </div>

        {/* Board / Project */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="sm" className="h-7 px-2 text-xs gap-1.5">
              <Kanban className="h-3 w-3" />
              {selectedBoard?.name || t('tasks:fields.board')}
            </Button>
          </DropdownMenuTrigger>
          {/* 300px is the design ceiling; the `var()` is the cap that makes the
              last boards reachable. The menu portals out of the dialog, so it is
              positioned against the VIEWPORT — a pinned height runs past the
              bottom of a short screen and this element, the scroll container, is
              what ends up off-screen. */}
          <DropdownMenuContent
            align="start"
            className="max-h-[min(300px,var(--radix-dropdown-menu-content-available-height,300px))] overflow-y-auto"
          >
            {boards.map((board: Board) => (
              <DropdownMenuItem
                key={board.id}
                className="gap-2"
                onSelect={() => setSelectedBoardId(board.id)}
              >
                <div
                  className="h-3 w-3 rounded-sm shrink-0"
                  style={{ backgroundColor: board.color || 'hsl(var(--primary))' }}
                />
                <BidiText className="truncate">{board.name}</BidiText>
                {board.id === selectedBoardId && <Check className="h-3.5 w-3.5 ms-auto shrink-0" />}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>

        {/* Due Date */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="sm" className="h-7 px-2 text-xs gap-1.5">
              <CalendarDays className="h-3 w-3" />
              {dueDate ? (
                <>
                  {formatPlanningDate(dueDate, 'short')}
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); setDueDate(undefined); }}
                    className="hover:text-foreground ms-0.5"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </>
              ) : (
                t('tasks:fields.dueDate')
              )}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="p-0">
            <Calendar
              mode="single"
              selected={dueDate}
              onSelect={(date) => { setDueDate(date); }}
              initialFocus
            />
          </DropdownMenuContent>
        </DropdownMenu>

        {/* Estimate (shown after toggle) */}
        {showEstimate && (
          <div className="inline-flex items-center gap-1">
            <Button variant="outline" size="sm" className="h-7 px-2 text-xs gap-1.5 cursor-default hover:bg-transparent">
              <Timer className="h-3 w-3" />
              <Input
                type="number"
                min={0}
                step={0.5}
                value={estimatedHours ?? ''}
                onChange={(e) => setEstimatedHours(e.target.value ? Number(e.target.value) : undefined)}
                placeholder="0"
                className="h-5 w-12 border-none bg-transparent p-0 text-xs shadow-none focus-visible:ring-0"
              />
              <span className="text-muted-foreground text-xs">{t('tasks:units.hours')}</span>
              <button
                type="button"
                onClick={() => { setShowEstimate(false); setEstimatedHours(undefined); }}
                className="hover:text-foreground text-muted-foreground"
              >
                <X className="h-3 w-3" />
              </button>
            </Button>
          </div>
        )}

        {/* Progress (shown after toggle) */}
        {showProgress && (
          /* The `%` is a symbol, not a word: pin the whole progress chip LTR so the row
             cannot flip it onto the digits under RTL — see NumericFieldBox. */
          <NumericFieldBox className="inline-flex items-center gap-1">
            <Button variant="outline" size="sm" className="h-7 px-2 text-xs gap-1.5 cursor-default hover:bg-transparent">
              <TrendingUp className="h-3 w-3" />
              <Input
                type="number"
                min={0}
                max={100}
                value={progress ?? ''}
                onChange={(e) => {
                  const val = Number(e.target.value);
                  setProgress(Math.min(100, Math.max(0, val || 0)));
                }}
                placeholder="0"
                className="h-5 w-12 border-none bg-transparent p-0 text-xs shadow-none focus-visible:ring-0"
              />
              <span className="text-muted-foreground text-xs">%</span>
              <button
                type="button"
                onClick={() => { setShowProgress(false); setProgress(undefined); }}
                className="hover:text-foreground text-muted-foreground"
              >
                <X className="h-3 w-3" />
              </button>
            </Button>
          </NumericFieldBox>
        )}

        {/* Attachments */}
        <input
          ref={fileInputRef}
          type="file"
          multiple
          className="hidden"
          onChange={(e) => {
            if (e.target.files?.length) {
              setAttachments((prev) => [...prev, ...Array.from(e.target.files!)]);
              e.target.value = '';
            }
          }}
        />
        <Button
          variant="outline"
          size="sm"
          className="h-7 px-2 text-xs gap-1.5"
          onClick={() => fileInputRef.current?.click()}
        >
          <Paperclip className="h-3 w-3" />
          {attachments.length > 0
            ? t('tasks:attachments.count', { count: attachments.length })
            : t('tasks:attachments.title')}
        </Button>
        {attachments.length > 0 && (
          <button
            type="button"
            onClick={() => setAttachments([])}
            className="h-7 w-7 flex items-center justify-center text-muted-foreground hover:text-foreground -ms-1"
          >
            <X className="h-3 w-3" />
            <span className="sr-only">{t('tasks:attachments.delete')}</span>
          </button>
        )}

        {/* More actions */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="sm" className="h-7 w-7 p-0">
              <MoreHorizontal className="h-3.5 w-3.5" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start">
            {!showEstimate && (
              <DropdownMenuItem className="gap-2" onSelect={() => setShowEstimate(true)}>
                <Timer className="h-4 w-4" />
                {t('tasks:fields.estimate')}
              </DropdownMenuItem>
            )}
            {!showProgress && (
              <DropdownMenuItem className="gap-2" onSelect={() => setShowProgress(true)}>
                <TrendingUp className="h-4 w-4" />
                {t('tasks:fields.progress')}
              </DropdownMenuItem>
            )}
            <DropdownMenuItem
              className="gap-2"
              onSelect={() => {
                setShowSubtasks(true);
                setSubtaskTitles((prev) => [...prev, '']);
                requestAnimationFrame(() => subtaskInputRef.current?.focus());
              }}
            >
              <ListPlus className="h-4 w-4" />
              {t('tasks:addSubtask')}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Subtasks section */}
      {showSubtasks && subtaskTitles.length > 0 && (
        <div className="flex flex-col gap-1 px-5 py-2 border-t border-border">
          <span className="text-xs text-muted-foreground mb-0.5">{t('tasks:settings.subtasks')}</span>
          {subtaskTitles.map((subtask, index) => (
            <div key={index} className="flex items-center gap-1.5">
              <Circle className="h-3 w-3 text-muted-foreground/40 shrink-0" />
              <input
                ref={index === subtaskTitles.length - 1 ? subtaskInputRef : undefined}
                value={subtask}
                onChange={(e) => {
                  const updated = [...subtaskTitles];
                  updated[index] = e.target.value;
                  setSubtaskTitles(updated);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && subtask.trim()) {
                    e.preventDefault();
                    e.stopPropagation();
                    setSubtaskTitles((prev) => [...prev, '']);
                    requestAnimationFrame(() => subtaskInputRef.current?.focus());
                  }
                  if (e.key === 'Backspace' && !subtask && subtaskTitles.length > 1) {
                    e.preventDefault();
                    const updated = subtaskTitles.filter((_, i) => i !== index);
                    setSubtaskTitles(updated);
                  }
                }}
                placeholder={t('tasks:fields.title')}
                className="flex-1 bg-transparent text-sm outline-none border-none placeholder:text-muted-foreground/40"
              />
              <button
                type="button"
                onClick={() => {
                  const updated = subtaskTitles.filter((_, i) => i !== index);
                  if (updated.length === 0) setShowSubtasks(false);
                  setSubtaskTitles(updated);
                }}
                className="text-muted-foreground hover:text-foreground"
              >
                <X className="h-3 w-3" />
              </button>
            </div>
          ))}
          <button
            type="button"
            onClick={() => {
              setSubtaskTitles((prev) => [...prev, '']);
              requestAnimationFrame(() => subtaskInputRef.current?.focus());
            }}
            className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground mt-0.5"
          >
            <Plus className="h-3 w-3" />
            {t('tasks:addSubtask')}
          </button>
        </div>
      )}

      {/* Footer with create button */}
      <div className="flex items-center justify-end px-4 py-3 border-t border-border">
        <div className="flex items-center gap-3">
          {/* Create button */}
          <Button
            size="sm"
            onClick={handleSubmit}
            disabled={!title.trim() || createMutation.isPending}
          >
            {createMutation.isPending && <Loader2 className="h-3.5 w-3.5 me-1.5 animate-spin" />}
            {t('common:app.create')}
          </Button>
        </div>
      </div>
    </div>
  );

  return (
    <DialogForm
      open={open}
      onClose={onClose}
      title={t('tasks:tasks.create')}
      hideActions
      maxWidth="2xl"
      autoFocusFirst={false}
    >
      <div ref={dialogBodyRef}>{formContent}</div>
    </DialogForm>
  );
}

